import { useEffect, useRef, useState } from 'react';
import type { DocType } from '@/types/company';
import { SIGNED_RESULT_DOC_TYPES } from '@/lib/accreditation';
import { playSignedSound } from '@/lib/notificationSound';

const EMPTY: ReadonlySet<DocType> = new Set();
// Держим флаг «только что подписан» дольше самой анимации всплеска (~560 мс), потом снимаем —
// чтобы всплеск проиграл один раз и мог сработать снова при будущем повторном подписании.
const HIGHLIGHT_MS = 900;

interface HasDocType {
  readonly docType: DocType;
}

/**
 * Возвращает набор итоговых docType (SIGNED_*), которые ТОЛЬКО ЧТО стали подписаны в этой сессии —
 * для однократной анимации бейджа. Тонкости:
 * - на первом показе с данными не срабатывает (документ мог быть подписан ранее → показываем тихо);
 * - триггерится, когда новый SIGNED_*-тип появляется в данных (обычно по SSE-инвалидации кэша);
 * - исчезновение подписанного типа (пересоздание договора/анкеты) анимацию не запускает.
 */
export function useJustSignedDocTypes(groups: readonly HasDocType[] | undefined): ReadonlySet<DocType> {
  const [justSigned, setJustSigned] = useState<ReadonlySet<DocType>>(EMPTY);
  const prevSignedRef = useRef<Set<DocType> | null>(null);

  // Устойчивая сигнатура набора подписанных типов (null — данных ещё нет). Эффект зависит от неё,
  // а не от ссылки на массив, чтобы не срабатывать на каждый ре-рендер.
  const signature =
    groups === undefined
      ? null
      : [...new Set(groups.map((group) => group.docType).filter((docType) => SIGNED_RESULT_DOC_TYPES.has(docType)))]
          .sort()
          .join(',');

  useEffect(() => {
    if (signature === null) {
      return undefined;
    }
    const current = new Set<DocType>(signature === '' ? [] : (signature.split(',') as DocType[]));
    const previous = prevSignedRef.current;
    prevSignedRef.current = current;
    if (previous === null) {
      return undefined; // первый показ с данными — без анимации
    }
    const fresh = [...current].filter((docType) => !previous.has(docType));
    if (fresh.length === 0) {
      return undefined;
    }
    // Звуковой сигнал сопровождает тот же момент, что и визуальный всплеск бейджа (один на подписание —
    // движок ограничивает частоту и уважает переключатель «звук уведомлений»).
    playSignedSound();
    setJustSigned(new Set(fresh));
    const timer = setTimeout(() => setJustSigned(EMPTY), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [signature]);

  return justSigned;
}
